// /letter/$letterId — single-letter detail view. Marks the letter as
// read on mount (idempotent via the API) and renders the full body
// without a reply composer; the composer lives on /home where the full
// thread context is available. From here the user can return to the
// inbox or jump back to /home to reply.

import type { BondDto, LetterDto } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { switchLocaleToBond } from '../i18n/index.js';
import { getLetter, getMyBond, markLetterRead } from '../lib/api';
import { useAdaptivePoll } from '../lib/use-adaptive-poll';
import { authClient, getSessionSafe } from '../lib/auth-client';
import {
  type EchoformTreatment,
  getLetterPortrait,
  letterExcerpt,
  letterTypeLabel,
  relativeTime,
} from '../lib/letter-format';
import { useEmberTheme } from '../lib/use-ember-theme';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/letter/$letterId')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async ({ params }) => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    const letter = await getLetter(params.letterId);
    return { bond, letter };
  },
  component: LetterDetailPage,
});

function LetterDetailPage() {
  const { t } = useTranslation();
  const initial = Route.useLoaderData();
  const [bond] = useState<BondDto>(initial.bond);
  const [letter, setLetter] = useState<LetterDto>(initial.letter);

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // Sync state to the loader's letter when navigating between letter
  // IDs (e.g. clicking the chain block's "{Ember} wrote back" card to
  // jump to the next letter). useState only initialises once on mount;
  // without this sync the component would keep rendering the previous
  // letter even though the loader has already fetched the new one.
  useEffect(() => {
    setLetter(initial.letter);
  }, [initial.letter]);

  // Mark read on mount. Idempotent server-side, so no guard needed if
  // the user navigates back later — the server returns the existing
  // read_at and we update local state to match.
  useEffect(() => {
    if (letter.readAt) return;
    let cancelled = false;
    (async () => {
      try {
        const { readAt } = await markLetterRead(letter.id);
        if (!cancelled) setLetter((prev) => ({ ...prev, readAt }));
      } catch {
        // Soft-fail — the letter still renders. The user can refresh
        // if read state matters to them, and the next /inbox visit
        // will auto-mark it on next open anyway.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [letter.id, letter.readAt]);

  // Poll for chain completion. Active only while the user has replied
  // but the Ember hasn't written back yet — Phase 2 made replies take
  // up to several hours, so the polling pauses when the tab is hidden
  // and steps down through 8 s → 30 s → 2 min as the wait grows. Stops
  // entirely once the next letter lands.
  const awaitingNextEmber = Boolean(
    letter.chain?.userReply && !letter.chain?.nextEmberLetter,
  );
  const refreshLetter = useCallback(async () => {
    const fresh = await getLetter(letter.id);
    setLetter((prev) => ({
      ...fresh,
      readAt: prev.readAt ?? fresh.readAt ?? null,
    }));
  }, [letter.id]);
  useAdaptivePoll(refreshLetter, { enabled: awaitingNextEmber });

  const paragraphs = letter.body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const label = letterTypeLabel(letter.letterType);
  const portrait = getLetterPortrait(letter, bond);

  return (
    <main className="mx-auto max-w-2xl">
      <nav className="mt-12 mb-10 font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.34em] sm:mt-8 sm:text-xs lg:mt-4">
        <Link
          to="/inbox"
          className="transition hover:text-ash-200"
        >
          {t('letterDetail.backToInbox')}
        </Link>
      </nav>

      {letter.parent && letter.parent.kind === 'user' && (
        <p className="mb-8 rounded-sm border border-ash-700/30 bg-paper-900/30 p-4 font-body text-ash-300 text-sm italic leading-relaxed lg:mb-10 lg:p-5 lg:text-[0.95rem]">
          <span className="font-display text-[0.6rem] text-ash-500 uppercase not-italic tracking-[0.34em]">
            {t('letterDetail.inReplyToYourLetter', {
              when: relativeTime(letter.parent.at),
            })}
          </span>
          <br />
          <span className="mt-1 block">{letterExcerpt(letter.parent.snippet)}</span>
        </p>
      )}

      <header className="mb-10 text-center animate-fadeUp lg:mb-14">
        {portrait.url && (
          <figure className="mx-auto mb-6 w-28 sm:w-32 lg:mb-8 lg:w-36">
            <img
              src={portrait.url}
              alt={bond.emberName}
              className="ember-portrait-mask block w-full select-none"
              draggable={false}
            />
          </figure>
        )}
        <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem]">
          {label}
        </p>
        <h1
          className="mt-4 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
          style={{ fontWeight: 500 }}
        >
          {t('letterDetail.fromEmber', { name: bond.emberName })}
        </h1>
        <p className="mt-3 font-body text-ash-400 text-sm italic sm:text-base">
          {relativeTime(letter.generatedAt)}
        </p>
        <div className="mx-auto mt-7 flex max-w-xs items-center gap-3 lg:mt-9">
          <span className="hairline h-px flex-1" />
          <span aria-hidden="true" className="block h-1 w-1 rounded-full bg-accent" />
          <span className="hairline h-px flex-1" />
        </div>
      </header>

      <article
        className={`letter animate-fadeUp text-[1.06rem] text-ash-100 sm:text-[1.12rem] lg:text-[1.18rem] ${treatmentClass(portrait.treatment)}`}
        style={{ animationDelay: '0.2s' }}
      >
        {paragraphs.map((p) => (
          <p key={p.slice(0, 64)}>{p}</p>
        ))}
      </article>

      {letter.pendingChoice && !letter.chain?.userReply && (
        <section
          className="mt-12 rounded-sm border border-accent/40 bg-paper-900/40 p-6 animate-fadeUp lg:mt-16 lg:p-8"
          style={{ animationDelay: '0.4s' }}
        >
          <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.34em]">
            {t('letterDetail.choice.heading', { name: bond.emberName })}
          </p>
          <ul className="mt-4 space-y-2 font-body text-ash-200 italic">
            {letter.pendingChoice.options.map((opt) => (
              <li key={opt} className="before:mr-2 before:text-accent before:content-['◇']">
                {opt}
              </li>
            ))}
          </ul>
        </section>
      )}

      {letter.chain?.userReply && (
        <ChainBlock
          userReply={letter.chain.userReply}
          nextEmberLetter={letter.chain.nextEmberLetter ?? null}
          emberName={bond.emberName}
        />
      )}

      <LetterFooter
        letterId={letter.id}
        hasUserReply={Boolean(letter.chain?.userReply)}
        hasNextEmber={Boolean(letter.chain?.nextEmberLetter)}
        emberName={bond.emberName}
      />
    </main>
  );
}

// Map echoform treatment → optional Tailwind classes on the article
// container. Memory shard letters get a subtle warm sepia tint via a
// linear-gradient overlay — not a different design, just a different
// register of the same design. Dream/Deep slots reserved for when
// those letter pipelines exist; default Awakened adds nothing.
function treatmentClass(treatment: EchoformTreatment): string {
  switch (treatment) {
    case 'memory':
      return 'letter-treatment-memory';
    case 'dream':
      return 'letter-treatment-dream';
    case 'deep':
      return 'letter-treatment-deep';
    default:
      return '';
  }
}

function ChainBlock({
  userReply,
  nextEmberLetter,
  emberName,
}: {
  userReply: NonNullable<LetterDto['chain']>['userReply'];
  nextEmberLetter: NonNullable<LetterDto['chain']>['nextEmberLetter'];
  emberName: string;
}) {
  const { t } = useTranslation();
  if (!userReply) return null;
  return (
    <section
      className="mt-14 animate-fadeUp lg:mt-20"
      style={{ animationDelay: '0.4s' }}
    >
      <div className="mb-6 flex items-center gap-4 lg:mb-8">
        <span className="hairline h-px flex-1" />
        <span className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.4em]">
          {t('letterDetail.chainHeading')}
        </span>
        <span className="hairline h-px flex-1" />
      </div>

      <article className="rounded-sm border border-accent/30 border-l-2 bg-paper-900/30 p-5 lg:p-6">
        <p className="font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.34em]">
          {t('letterDetail.youReplied', { when: relativeTime(userReply.at) })}
        </p>
        <p className="mt-2 font-body text-ash-200 text-sm italic leading-relaxed sm:text-[0.95rem] lg:text-[1rem]">
          “{letterExcerpt(userReply.snippet)}”
        </p>
      </article>

      {nextEmberLetter ? (
        <Link
          to="/letter/$letterId"
          params={{ letterId: nextEmberLetter.id }}
          className={`group mt-5 block rounded-sm border p-5 transition lg:mt-6 lg:p-6 ${
            nextEmberLetter.isUnread
              ? 'border-accent/50 bg-paper-900/50 hover:border-accent-strong/70 hover:bg-paper-700/50'
              : 'border-ash-700/40 bg-paper-900/30 hover:border-ash-600/60 hover:bg-paper-800/40'
          }`}
        >
          <p
            className={`flex items-center gap-3 font-display text-[0.6rem] uppercase tracking-[0.34em] ${
              nextEmberLetter.isUnread ? 'text-accent-strong' : 'text-ash-500'
            }`}
          >
            {nextEmberLetter.isUnread && (
              <span aria-hidden="true" className="block h-1.5 w-1.5 animate-pulseCore rounded-full bg-accent" />
            )}
            <span>
              {t('letterDetail.emberWroteBack', {
                name: emberName,
                when: relativeTime(nextEmberLetter.at),
              })}
            </span>
          </p>
          <p
            className={`mt-2 font-body text-sm leading-relaxed sm:text-[1rem] lg:text-[1.04rem] ${
              nextEmberLetter.isUnread ? 'text-ash-100' : 'text-ash-300'
            }`}
          >
            {letterExcerpt(nextEmberLetter.snippet)}
          </p>
          <p className="mt-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em] group-hover:text-ash-300">
            {nextEmberLetter.isUnread
              ? t('letterDetail.breakSeal')
              : t('letterDetail.readLetter')}
          </p>
        </Link>
      ) : (
        <p className="mt-5 px-2 font-body text-ash-500 text-sm italic lg:mt-6">
          {t('letterDetail.awaitingNext', { name: emberName })}
        </p>
      )}
    </section>
  );
}

function LetterFooter({
  letterId,
  hasUserReply,
  hasNextEmber,
  emberName: _emberName,
}: {
  letterId: string;
  hasUserReply: boolean;
  hasNextEmber: boolean;
  emberName: string;
}) {
  const { t } = useTranslation();
  // Three states:
  // 1. No user reply yet  → show "Reply to this letter" CTA
  // 2. Replied, no Ember response yet → quiet "Return to letters" only
  //    (the chain block already explains the wait)
  // 3. Full chain (replied + next Ember letter) → "Return to letters"
  //    only; the next-letter link inside ChainBlock is the forward door
  return (
    <footer className="mt-14 border-paper-800/50 border-t pt-8 text-center font-body text-ash-500 text-xs italic lg:mt-20 lg:text-sm">
      <div className="flex items-center justify-center gap-6">
        <Link to="/inbox" className="transition hover:text-ash-300">
          {t('letterDetail.footer.returnToLetters')}
        </Link>
        {!hasUserReply && (
          <>
            <span aria-hidden="true" className="text-ash-700">
              ·
            </span>
            <Link
              to="/write"
              search={{ inResponseTo: letterId }}
              className="transition hover:text-ash-300"
            >
              {t('letterDetail.footer.replyTo')}
            </Link>
          </>
        )}
        {hasUserReply && !hasNextEmber && (
          <>
            <span aria-hidden="true" className="text-ash-700">
              ·
            </span>
            <span className="text-ash-600">{t('letterDetail.footer.awaitingReply')}</span>
          </>
        )}
      </div>
    </footer>
  );
}
